import { useEffect, useMemo, useState } from 'react';

import moment from 'moment';

import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { MatterType } from '../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useAddUserToMatters } from '@/api/mutations/useAddUserToMatters';
import { useRemoveUserFromMatters } from '@/api/mutations/useRemoveUserFromMatters';
import { useFetchOrgUsers } from '@/api/queries/analyticsQueries/useFetchOrgUsers';
import { useGetMatters } from '@/api/queries/useGetMatters';
import { Matter } from '@/api/types/backend-dto-types';
import isPlural from '@/helpers/isPlural';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

interface MatterRow {
  id: string;
  name: string;
  code: string | null;
  meta: string;
  isMember: boolean;
  isCreator: boolean;
}

/** e.g. "12 docs · created 3 Mar 2025 by sam@firm.com" */
function buildMatterMeta(matter: Matter): string {
  const docs = `${matter.docsCount} ${isPlural({ one: 'doc', more: 'docs', number: matter.docsCount })}`;
  const created = `created ${moment(matter.createdDate).format('D MMM YYYY')}`;

  return [docs, matter.creatorEmail ? `${created} by ${matter.creatorEmail}` : created].join(' · ');
}

/**
 * Manage which Matters a user belongs to. Only safe to open for users with
 * Permission.MatterAccessAll — it lists every Matter in the organisation.
 */
export function ManageMattersDialog({
  userId,
  organisationId,
  open,
  onOpenChange,
}: {
  userId: string | null;
  organisationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [search, setSearch] = useState('');
  // Which Matter has a membership change in flight — one at a time, so every row's control is disabled while it settles
  const [pendingMatterId, setPendingMatterId] = useState<string | null>(null);

  const { data: users } = useFetchOrgUsers(organisationId);
  const { data: matters, isLoading: isMattersLoading } = useGetMatters();

  const { mutate: addUserToMatters } = useAddUserToMatters();
  const { mutate: removeUserFromMatters } = useRemoveUserFromMatters();

  // Read the user back from the cached list so it stays fresh after a mutation
  const selectedUser = users?.find((u) => u.id === userId) ?? null;

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setPendingMatterId(null);
  }, [open, userId]);

  // Members first, then alphabetical — the user's Matters are what an admin came to check
  const matterRows: MatterRow[] = useMemo(() => {
    const memberMatterIds = new Set((selectedUser?.matters ?? []).map((matter) => matter.id));

    return (matters ?? [])
      .filter((matter) => !matter.isDraft && matter.type !== MatterType.Personal)
      .map((matter) => ({
        id: matter.id,
        name: matter.name,
        code: matter.code ?? null,
        meta: buildMatterMeta(matter),
        isMember: memberMatterIds.has(matter.id),
        isCreator: matter.createdById === selectedUser?.id,
      }))
      .sort((a, b) => Number(b.isMember) - Number(a.isMember) || a.name.localeCompare(b.name));
  }, [matters, selectedUser]);

  const visibleMatterRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return matterRows;

    return matterRows.filter(
      (matter) => matter.name.toLowerCase().includes(query) || matter.code?.toLowerCase().includes(query),
    );
  }, [matterRows, search]);

  const memberCount = matterRows.filter((matter) => matter.isMember).length;

  const handleAdd = (matterId: string) => {
    if (!selectedUser || pendingMatterId) return;

    setPendingMatterId(matterId);
    addUserToMatters({ userId: selectedUser.id, matterIds: [matterId] }, { onSettled: () => setPendingMatterId(null) });
    trackCustomEvent(TrackingEventsV2.MatterAddUser);
  };

  const handleRemove = (matterId: string) => {
    if (!selectedUser || pendingMatterId) return;

    setPendingMatterId(matterId);
    removeUserFromMatters(
      { userId: selectedUser.id, matterIds: [matterId] },
      { onSettled: () => setPendingMatterId(null) },
    );
    trackCustomEvent(TrackingEventsV2.MatterRemoveUser);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage Matters</DialogTitle>
          <DialogDescription>
            Add {selectedUser?.email} to any Matter in your organisation, or remove their access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <SearchInput
            placeholder="Search Matters by name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mt-5"
          />

          <div className="mt-6">
            <div className="px-3 pb-2 text-sm font-medium text-gray-600">
              Matters{' '}
              <span className="font-normal text-gray-400">
                (member of {memberCount} of {matterRows.length})
              </span>
            </div>

            <hr />

            <div className="max-h-128 min-h-32 divide-y divide-gray-200 overflow-y-auto">
              {isMattersLoading ? (
                <div className="flex h-32 items-center justify-center text-gray-400">
                  <Icon className="lucide--loader animate-spin ease-in-out" />
                </div>
              ) : visibleMatterRows.length === 0 ? (
                <div className="flex h-32 items-center justify-center px-3 py-1 text-center text-gray-400">
                  <p className="max-w-sm text-balance">
                    {matterRows.length === 0
                      ? 'There are no Matters in your organisation yet.'
                      : 'No Matters match your search.'}
                  </p>
                </div>
              ) : (
                visibleMatterRows.map((matter) => (
                  <div key={matter.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="truncate text-gray-700">
                          {matter.name} {matter.code && <kbd className="ml-1">{matter.code}</kbd>}
                        </p>
                        {matter.isMember && (
                          <Chip
                            className={cn(
                              'shrink-0 gap-1 rounded-full bg-sky-50 px-1.5 py-px pr-2 text-[11px] text-sky-500',
                            )}
                          >
                            <Icon className="lucide--user-2" />
                            Member
                          </Chip>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-gray-500">{matter.meta}</p>
                    </div>

                    {matter.isCreator ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500"
                            tabIndex={0}
                          >
                            <Icon className="lucide--crown" />
                            Matter Creator
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[18rem] text-center">
                          This user created the Matter, so their access cannot be removed.
                        </TooltipContent>
                      </Tooltip>
                    ) : matter.isMember ? (
                      <PermissionGate
                        permission={Permission.MatterRemoveUsers}
                        uiMode="disabled"
                        disabledMessage="You do not have permission to remove users from Matters"
                        side="left"
                      >
                        <Button
                          variant="destructiveGhost"
                          size="sm"
                          className="shrink-0"
                          disabled={pendingMatterId !== null}
                          onClick={() => handleRemove(matter.id)}
                          aria-label={`Remove access to ${matter.name}`}
                        >
                          <Icon
                            className={
                              pendingMatterId === matter.id ? 'lucide--loader animate-spin ease-in-out' : 'lucide--x'
                            }
                          />
                          Remove
                        </Button>
                      </PermissionGate>
                    ) : (
                      <PermissionGate
                        permission={Permission.MatterAddUsers}
                        uiMode="disabled"
                        disabledMessage="You do not have permission to add users to Matters"
                        side="left"
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                          className="shrink-0"
                          disabled={pendingMatterId !== null}
                          onClick={() => handleAdd(matter.id)}
                          aria-label={`Add to ${matter.name}`}
                        >
                          <Icon
                            className={
                              pendingMatterId === matter.id ? 'lucide--loader animate-spin ease-in-out' : 'lucide--plus'
                            }
                          />
                          Add
                        </Button>
                      </PermissionGate>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="primary" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
